import { createResourceCollection } from '@/request'
import { UtilityProgramApplication } from '@/types/utilityProgramApplication'
import {
  ProgramRequirementProps,
  ProgramRequirementStep,
} from '@/types/utilityProgramProfileEligibility'
import { AppDispatch } from '@/store'

export const utilityProgramApplicationsCollection =
  createResourceCollection<UtilityProgramApplication>({
    name: 'utilityProgramApplications',
    selector: (state) => state.utilityProgramApplications,
    apiConfig: {
      path: 'utility_programs',
    },
  })

export const useHasExistingApplication = (utilityProgramId: number) => {
  const { data } = utilityProgramApplicationsCollection.useFetch(
    `${utilityProgramId}/application_progress`,
  )
  return data.length > 0
}

/**
 * Posts the user's furthest progress to the backend and invalidates the
 * collection so subsequent fetches see the latest state.
 *
 * `current_step` is the max of the first-incomplete-step index and the step
 * the user is navigating to. The latter is required because callers fire this
 * immediately after dispatching the redux update that marks the just-finished
 * step complete — `programRequirements` captured here is still pre-update, so
 * relying on it alone would under-report progress on the final step.
 */
export const postApplicationProgress = async (
  dispatch: AppDispatch,
  utilityProgramId: number,
  programRequirements: ProgramRequirementProps,
  navigatingToStep: ProgramRequirementStep,
) => {
  const steps = programRequirements.steps
  const firstIncompleteIndex = steps.findIndex((s) => !s.completed)
  const fallbackIndex =
    firstIncompleteIndex === -1 ? steps.length - 1 : firstIncompleteIndex
  const navigatingToIndex = steps.indexOf(navigatingToStep)
  const stepIndex = Math.max(fallbackIndex, navigatingToIndex)
  const currentStep = steps[stepIndex]
  await dispatch(
    utilityProgramApplicationsCollection.actions.create(
      {
        current_step: stepIndex + 1,
        current_step_name: currentStep.name,
        total_steps: programRequirements.numberOfSteps,
      },
      {
        pathSegments: [utilityProgramId.toString(), 'application_progress'],
      },
    ),
  )
  dispatch(utilityProgramApplicationsCollection.actions.invalidate())
}

export default utilityProgramApplicationsCollection.slice.reducer
